import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { AUTH_ENDPOINTS, SSO_API_BASE_URL } from '../api/endpoints';
import { TOKEN_TYPE, setTokenExpiry, setTokenToCookies } from '@/lib/cookies';
import { getRootDomain } from '@/lib/domain';
import { redirectToSSOLogin } from '@/lib/sso';

function getCookieDomain(): string | undefined {
  const root = getRootDomain(window.location.hostname);
  return root ? `.${root}` : undefined;
}

function safeReturnTo(value: string | null): string {
  if (!value) return '/';
  try {
    const decoded = decodeURIComponent(value);
    return decoded.startsWith('/') && !decoded.startsWith('//') ? decoded : '/';
  } catch {
    return '/';
  }
}

export const AuthCallbackPage: React.FC = () => {
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const processing = useRef(false);
  const code = params.get('code');
  const returnTo = safeReturnTo(params.get('returnTo'));

  const exchangeCode = async () => {
    if (!code) {
      redirectToSSOLogin(returnTo);
      return;
    }

    setError(null);
    try {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 10000);
      const response = await fetch(`${SSO_API_BASE_URL}${AUTH_ENDPOINTS.exchangeCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
        signal: controller.signal,
      });
      window.clearTimeout(timeoutId);
      const result = await response.json() as {
        success?: boolean;
        message?: string;
        data?: { access_token: string; refresh_token: string; expires_in: number };
      };

      if (!response.ok || !result.success || !result.data) {
        throw new Error(result.message || 'Mã xác thực không hợp lệ hoặc đã hết hạn.');
      }

      const domain = getCookieDomain();
      setTokenToCookies(TOKEN_TYPE.ACCESS_TOKEN, result.data.access_token, result.data.expires_in, domain);
      setTokenToCookies(TOKEN_TYPE.REFRESH_TOKEN, result.data.refresh_token, 7 * 24 * 60 * 60, domain);
      setTokenExpiry(new Date(Date.now() + result.data.expires_in * 1000).toISOString(), domain);
      window.location.replace(returnTo);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Không thể hoàn tất đăng nhập.');
    }
  };

  useEffect(() => {
    if (processing.current) return;
    processing.current = true;
    void exchangeCode();
  }, [code]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl">
          <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-red-500" />
          <h1 className="mb-2 text-xl font-semibold text-gray-900">Xác thực thất bại</h1>
          <p className="mb-6 text-sm text-gray-600">{error}</p>
          <button onClick={() => void exchangeCode()} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white">
            <RefreshCw className="h-4 w-4" /> Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-gray-50">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      <p className="text-sm text-gray-600">Đang hoàn tất đăng nhập...</p>
    </div>
  );
};
